import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../styles/AffiliateSignup.module.css';

export default function AffiliateSignup() {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_affiliate_name: ''
  });

  const [existingAffiliates, setExistingAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [errors, setErrors] = useState({});
  const AFFILIATE_API_URL = 'https://rody753ay0.execute-api.us-east-1.amazonaws.com/dev';

  // Load existing affiliates on component mount
  useEffect(() => {
    const loadExistingAffiliates = async () => {
      try {
        const response = await fetch(`${AFFILIATE_API_URL}/affiliate`);
        if (response.ok) {
          const data = await response.json();
          setExistingAffiliates(data.data?.affiliates || []);
        }
      } catch (error) {
        console.warn('Failed to load existing affiliates:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingAffiliates();
  }, []);

  // Generate QR code when affiliate data is available
  useEffect(() => {
    if (affiliateData && affiliateData.username) {
      const referralUrl = `https://musuko.co/getstarted?r=${affiliateData.username}`;
      
      QRCode.toDataURL(referralUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#1a1a2e',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'M'
      })
      .then(url => {
        setQrCodeDataUrl(url);
      })
      .catch(error => {
        console.error('Failed to generate QR code:', error);
      });
    }
  }, [affiliateData]);

  // Add body overflow class when component mounts
  useEffect(() => {
    document.body.classList.add('affiliate-page');
    return () => {
      document.body.classList.remove('affiliate-page');
    };
  }, []);

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (form.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]{10,15}$/.test(form.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!form.preferred_affiliate_name.trim()) {
      newErrors.preferred_affiliate_name = 'Username is required';
    } else if (form.preferred_affiliate_name.trim().length < 3) {
      newErrors.preferred_affiliate_name = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(form.preferred_affiliate_name.trim())) {
      newErrors.preferred_affiliate_name = 'Username can only contain letters, numbers, and underscores';
    } else if (existingAffiliates.some(affiliate => 
      affiliate.username.toLowerCase() === form.preferred_affiliate_name.trim().toLowerCase()
    )) {
      newErrors.preferred_affiliate_name = 'This username is already taken. Please choose another.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${AFFILIATE_API_URL}/affiliate/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          preferred_affiliate_name: form.preferred_affiliate_name.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors) {
          setErrors(errorData.errors);
        } else {
          throw new Error(errorData.message || 'Failed to create affiliate account');
        }
        return;
      }

      const data = await response.json();
      setAffiliateData(data.data);
      setSubmitted(true);

      // Scroll to top of success page
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error creating affiliate account:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;

    const link = document.createElement('a');
    link.download = `musuko-referral-${affiliateData.username}.png`;
    link.href = qrCodeDataUrl;
    link.click();
  };

  const copyReferralUrl = () => {
    const referralUrl = `https://musuko.co/getstarted?r=${affiliateData.username}`;
    navigator.clipboard.writeText(referralUrl).then(() => {
      // Show temporary success message
      const button = document.querySelector(`.${styles.copyButton}`);
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        button.style.background = '#10b981';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
        }, 2000);
      }
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading affiliate system...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted && affiliateData) {
    const referralUrl = `https://musuko.co/getstarted?r=${affiliateData.username}`;
    
    return (
      <div className={styles.container}>
        <div className={styles.successContainer}>
          <div className={styles.successContent}>
            {/* Success Header */}
            <div className={styles.successHeader}>
              <div className={styles.celebrationContainer}>
                <div className={styles.successIcon}>
                  <FontAwesomeIcon icon="check-circle" />
                </div>
                <div className={styles.sparkles}>
                  <div className={styles.sparkle}></div>
                  <div className={styles.sparkle}></div>
                  <div className={styles.sparkle}></div>
                  <div className={styles.sparkle}></div>
                  <div className={styles.sparkle}></div>
                  <div className={styles.sparkle}></div>
                </div>
              </div>
              <h1 className={styles.successTitle}>🎉 Welcome to the Musuko Family! 🎉</h1>
              <p className={styles.successSubtitle}>
                Your affiliate account has been created successfully!
              </p>
              <div className={styles.emailConfirmation}>
                <FontAwesomeIcon icon="envelope" className={styles.emailIcon} />
                <span>Confirmation details have been sent to your email</span>
              </div>
            </div>

            {/* Affiliate Info */}
            <div className={styles.affiliateInfo}>
              <div className={styles.infoCard}>
                <h3>Your Affiliate Details</h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Name:</span>
                    <span className={styles.infoValue}>{affiliateData.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Username:</span>
                    <span className={styles.infoValue}>@{affiliateData.username}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Email:</span>
                    <span className={styles.infoValue}>{affiliateData.email}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Affiliate ID:</span>
                    <span className={styles.infoValue}>#{affiliateData.id}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Referral URL Section */}
            <div className={styles.referralSection}>
              <h3>Your Referral Link</h3>
              <p className={styles.referralDescription}>
                Share this link to earn commissions from every successful referral
              </p>
              
              <div className={styles.urlContainer}>
                <div className={styles.urlDisplay}>
                  <FontAwesomeIcon icon="link" className={styles.urlIcon} />
                  <span className={styles.url}>{referralUrl}</span>
                </div>
                <button 
                  className={styles.copyButton}
                  onClick={copyReferralUrl}
                >
                  <FontAwesomeIcon icon="copy" />
                  Copy Link
                </button>
              </div>
            </div>

            {/* QR Code Section */}
            <div className={styles.qrSection}>
              <h3>QR Code</h3>
              <p className={styles.qrDescription}>
                Save or share this QR code for easy referrals
              </p>
              
              <div className={styles.qrContainer}>
                {qrCodeDataUrl && (
                  <div className={styles.qrCodeWrapper}>
                    <img 
                      src={qrCodeDataUrl} 
                      alt="Referral QR Code" 
                      className={styles.qrCode}
                    />
                    <div className={styles.qrActions}>
                      <button 
                        className={styles.downloadButton}
                        onClick={downloadQRCode}
                      >
                        <FontAwesomeIcon icon="download" />
                        Download QR Code
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Next Steps */}
            <div className={styles.nextSteps}>
              <h3>What's Next?</h3>
              <div className={styles.stepsList}>
                <div className={styles.step}>
                  <div className={styles.stepIcon}>1</div>
                  <div className={styles.stepContent}>
                    <h4>Share Your Link</h4>
                    <p>Start sharing your referral link with potential customers</p>
                  </div>
                </div>
                <div className={styles.step}>
                  <div className={styles.stepIcon}>2</div>
                  <div className={styles.stepContent}>
                    <h4>Track Your Referrals</h4>
                    <p>Monitor your earnings and referrals in the affiliate dashboard</p>
                  </div>
                </div>
                <div className={styles.step}>
                  <div className={styles.stepIcon}>3</div>
                  <div className={styles.stepContent}>
                    <h4>Get Paid</h4>
                    <p>Receive commission payments for successful conversions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className={styles.actionButtons}>
              <button 
                className={styles.primaryActionButton}
                onClick={() => window.location.href = '/'}
              >
                <FontAwesomeIcon icon="home" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main signup form
  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FontAwesomeIcon icon="handshake" />
          </div>
          <h1 className={styles.title}>Join the Musuko Affiliate Program</h1>
          <p className={styles.subtitle}>
            Earn commissions by referring customers to our platform
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            {/* Name Field */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Full Name <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <FontAwesomeIcon icon="user" className={styles.inputIcon} />
                <input
                  type="text"
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
              </div>
              {errors.name && (
                <div className={styles.errorMessage}>
                  <FontAwesomeIcon icon="exclamation-circle" />
                  {errors.name}
                </div>
              )}
            </div>

            {/* Phone Field */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Phone Number <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <FontAwesomeIcon icon="phone" className={styles.inputIcon} />
                <input
                  type="tel"
                  className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                  placeholder="e.g. +1-555-123-4567"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
              {errors.phone && (
                <div className={styles.errorMessage}>
                  <FontAwesomeIcon icon="exclamation-circle" />
                  {errors.phone}
                </div>
              )}
            </div>

            {/* Email Field */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Email Address <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <FontAwesomeIcon icon="envelope" className={styles.inputIcon} />
                <input
                  type="email"
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  placeholder="Enter your email address"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
              </div>
              {errors.email && (
                <div className={styles.errorMessage}>
                  <FontAwesomeIcon icon="exclamation-circle" />
                  {errors.email}
                </div>
              )}
            </div>

            {/* Username Field */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Preferred Username <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <FontAwesomeIcon icon="at" className={styles.inputIcon} />
                <input
                  type="text"
                  className={`${styles.input} ${errors.preferred_affiliate_name ? styles.inputError : ''}`}
                  placeholder="Choose a unique username"
                  value={form.preferred_affiliate_name}
                  onChange={(e) => updateField('preferred_affiliate_name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                />
              </div>
              <div className={styles.usernameHelper}>
                Your referral link will be: musuko.co/getstarted?r={form.preferred_affiliate_name || 'username'}
              </div>
              {errors.preferred_affiliate_name && (
                <div className={styles.errorMessage}>
                  <FontAwesomeIcon icon="exclamation-circle" />
                  {errors.preferred_affiliate_name}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className={styles.submitSection}>
            <button
              type="submit"
              disabled={submitting}
              className={`${styles.submitButton} ${submitting ? styles.submitting : ''}`}
            >
              {submitting ? (
                <>
                  <div className={styles.spinner}></div>
                  Creating Account...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="rocket" />
                  Join Affiliate Program
                </>
              )}
            </button>
          </div>
        </form>

        {/* Benefits Section */}
        <div className={styles.benefits}>
          <h3>Why Join Our Affiliate Program?</h3>
          <div className={styles.benefitsList}>
            <div className={styles.benefit}>
              <FontAwesomeIcon icon="dollar-sign" className={styles.benefitIcon} />
              <div>
                <h4>Competitive Commissions</h4>
                <p>Earn generous commissions on every successful referral</p>
              </div>
            </div>
            <div className={styles.benefit}>
              <FontAwesomeIcon icon="chart-line" className={styles.benefitIcon} />
              <div>
                <h4>Real-Time Tracking</h4>
                <p>Monitor your referrals and earnings in real-time</p>
              </div>
            </div>
            <div className={styles.benefit}>
              <FontAwesomeIcon icon="headset" className={styles.benefitIcon} />
              <div>
                <h4>Dedicated Support</h4>
                <p>Get help from our affiliate support team</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
